import type { Filter, Query, QueryOrder } from "@cubejs-client/core";
import { Grain as grainSchema } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-meta";
import { err, isErr, isOk, ok, type Result } from "@hightouch/lib/util/result";
import { z } from "zod/v4";
import { parseCommaList } from "src/utils/comma-list";
import { MAX_FILTERED_ADS } from "src/pages/ad-studio/insights/performance/constants";
import {
  insightsDateRangeSchema,
  type InsightsDateRange,
} from "src/pages/ad-studio/insights/shared/dates";
import type {
  AdsQuery,
  DrillScopeArgs,
  SyncScope,
  EmbedFailure,
  EmbedNotice,
  EmbedPlan,
  EmbedRank,
  EmbedPlanState,
  EntityList,
  InsightsCubeQuery,
  InsightsMeta,
  InsightsMetaResult,
  PerformanceRow,
  Grain,
  MetricColumn,
  MetricKey,
  NonEmptyArray,
  ViewMeta,
} from "src/pages/ad-studio/insights/performance/types";
import {
  defaultSortOf,
  entityOf,
  initialSegmentsOf,
} from "src/pages/ad-studio/insights/performance/utils/meta";
import { noAdsMessage } from "src/pages/ad-studio/insights/shared/format";
import type { SortDirection } from "src/pages/ad-studio/insights/shared/types";

export type EmbedFilter =
  | { kind: "campaigns" | "ad-sets" | "ads"; ids: NonEmptyArray<string> }
  | { kind: "status" | "tag-value"; value: string };
export type EmbedAttrs = Readonly<Record<string, string>>;
type Picked<T> = { kind: "picked"; value: T } | { kind: "meta-default" };

const metricNamesSchema = z
  .string()
  .transform((raw) => raw.split(",").map((name) => name.trim()))
  .pipe(z.tuple([z.string().min(1)], z.string().min(1)));

const idsAttr = (kind: "campaigns" | "ad-sets") =>
  z
    .string()
    .optional()
    .transform((raw): readonly EmbedFilter[] => {
      const [first, ...rest] = parseCommaList(raw);
      return first === undefined ? [] : [{ kind, ids: [first, ...rest] }];
    });

const entityIdsAttr = z
  .string()
  .optional()
  .transform((raw) => parseCommaList(raw));

const tagFilterAttr = z
  .string()
  .optional()
  .transform((raw): readonly EmbedFilter[] =>
    raw === undefined || raw === "" ? [] : [{ kind: "tag-value", value: raw }],
  );

// Agents write statuses as the platforms show them ("ACTIVE"); the model stores them lowercase.
const statusAttr = z
  .string()
  .optional()
  .transform((raw): readonly EmbedFilter[] => {
    const status = (raw ?? "").trim().toLowerCase();
    return status === "" ? [] : [{ kind: "status", value: status }];
  });

const baselineAttr = z
  .string()
  .optional()
  .transform((raw): readonly EmbedNotice[] =>
    raw === undefined ? [] : ["baseline-not-supported"],
  );

const accountAttr = z
  .string()
  .optional()
  .transform((raw) => (raw === undefined ? "" : raw.trim()));

const sortAttr = z
  .string()
  .optional()
  .transform(
    (raw): Picked<string> =>
      raw === undefined
        ? { kind: "meta-default" }
        : { kind: "picked", value: raw.trim() },
  );

const SHARED_ATTRS = {
  "fivetran-sync-id": z.string().min(1),
  "account-id": accountAttr,
  "date-from": z.iso.date(),
  "date-to": z.iso.date(),
  "campaign-id": idsAttr("campaigns"),
  "ad-set-id": idsAttr("ad-sets"),
  "entity-ids": entityIdsAttr,
  status: statusAttr,
  "tag-filter": tagFilterAttr,
  "sort-by": sortAttr,
  "sort-direction": z.enum(["asc", "desc"]).default("desc"),
  baseline: baselineAttr,
};

type SharedAttrs = z.output<z.ZodObject<typeof SHARED_ATTRS>>;

const withDates = <Attrs extends { "date-from": string; "date-to": string }>(
  attrs: Attrs,
  context: z.RefinementCtx,
): Attrs & { dates: InsightsDateRange } => {
  const dates = insightsDateRangeSchema.safeParse([
    attrs["date-from"],
    attrs["date-to"],
  ]);
  if (!dates.success) {
    context.addIssue({
      code: "custom",
      message: "The range must not end before it starts",
      path: ["date-to"],
      continue: false,
    });
    return z.NEVER;
  }
  return { ...attrs, dates: dates.data };
};

const ENTITY_ID_FILTERS: Record<Grain, "campaigns" | "ad-sets" | "ads"> = {
  campaign: "campaigns",
  ad_set: "ad-sets",
  ad: "ads",
};

const toEmbedFilters = ({
  attrs,
  grain,
}: {
  attrs: SharedAttrs;
  grain: Grain;
}): readonly EmbedFilter[] => {
  const [first, ...rest] = attrs["entity-ids"];
  const entityIds: readonly EmbedFilter[] =
    first === undefined
      ? []
      : [{ kind: ENTITY_ID_FILTERS[grain], ids: [first, ...rest] }];
  return [
    ...attrs["campaign-id"],
    ...attrs["ad-set-id"],
    ...entityIds,
    ...attrs.status,
    ...attrs["tag-filter"],
  ];
};

/** An empty account id is the whole sync, as the embed attribute and the URL write it. */
export const toSyncScope = ({
  syncId,
  account,
}: {
  syncId: string;
  account: string;
}): SyncScope =>
  account === ""
    ? { kind: "by-sync", syncId }
    : { kind: "by-account", syncId, accounts: [account] };

const cardMetricsAttrs = z
  .object({
    metrics: metricNamesSchema.optional(),
    metric: z.string().optional(),
    "secondary-metrics": metricNamesSchema.optional(),
  })
  .transform(
    ({
      metrics,
      metric,
      "secondary-metrics": secondary,
    }): Picked<NonEmptyArray<string>> => {
      if (metrics !== undefined) {
        return { kind: "picked", value: metrics };
      }
      return metric === undefined
        ? { kind: "meta-default" }
        : { kind: "picked", value: [metric.trim(), ...(secondary ?? [])] };
    },
  );

const tableMetricsAttr = metricNamesSchema
  .optional()
  .transform(
    (metrics): Picked<NonEmptyArray<string>> =>
      metrics === undefined
        ? { kind: "meta-default" }
        : { kind: "picked", value: metrics },
  );

const EMBED_EMPTY_TABLE = "No results for the selected filters and date range.";

const toPlan = <List extends EntityList>({
  meta,
  list,
  attrs,
  placement,
}: {
  meta: InsightsMeta;
  list: List;
  attrs: SharedEmbedAttrs & {
    metrics: Picked<NonEmptyArray<string>>;
    pageSize: number;
    emptyMessage: string;
  };
  placement: MetricColumn["place"][number];
}): Result<EmbedPlan<List>, EmbedFailure> => {
  const { view } = list;
  const ranked = toRanked({ list, sort: attrs.sort });
  if (isErr(ranked)) {
    return ranked;
  }
  const query = toEmbedQuery({
    view,
    meta,
    attrs: { ...attrs, sort: ranked.value.sort },
    segments: initialSegmentsOf(view),
  });
  const metrics = toPickedMetrics({ view, picked: attrs.metrics, placement });
  if (isErr(query)) {
    return query;
  }
  if (isErr(metrics)) {
    return metrics;
  }
  return ok({
    list,
    query: query.value,
    rank: ranked.value.rank,
    metrics: metrics.value,
    meta,
    pageSize: attrs.pageSize,
    emptyMessage: attrs.emptyMessage,
    notices: attrs.notices,
  });
};

type SharedEmbedAttrs = EmbedQueryAttrs & { notices: readonly EmbedNotice[] };

const sharedOf = ({
  attrs,
  grain,
}: {
  attrs: SharedAttrs & { dates: InsightsDateRange };
  grain: Grain;
}): SharedEmbedAttrs => ({
  sync: toSyncScope({
    syncId: attrs["fivetran-sync-id"],
    account: attrs["account-id"],
  }),
  dates: attrs.dates,
  filters: toEmbedFilters({ attrs, grain }),
  sort: { picked: attrs["sort-by"], direction: attrs["sort-direction"] },
  notices: attrs.baseline,
});

const fatigueSortSchema = z.enum(["vs_peak", "peak_ctr"]);

const FATIGUE_SORT_MEMBERS = {
  vs_peak: "vsPeak",
  peak_ctr: "peakCtr",
} as const satisfies Record<z.output<typeof fatigueSortSchema>, string>;

const BY_QUERY_ORDER: EmbedRank = { kind: "by-query-order" };

/** A fatigue sort ranks an ad list by its fatigue member, then by the view's default metric. */
function toRanked({
  list,
  sort,
}: {
  list: EntityList;
  sort: EmbedSort;
}): Result<{ rank: EmbedRank; sort: EmbedSort }, EmbedFailure> {
  if (sort.picked.kind === "meta-default") {
    return ok({ rank: BY_QUERY_ORDER, sort });
  }
  const fatigueSort = fatigueSortSchema.safeParse(sort.picked.value);
  if (!fatigueSort.success) {
    return ok({ rank: BY_QUERY_ORDER, sort });
  }
  switch (list.kind) {
    case "ads": {
      return ok({
        rank: {
          kind: "by-member",
          member:
            list.view.layouts.fatigue[FATIGUE_SORT_MEMBERS[fatigueSort.data]],
          direction: sort.direction,
        },
        sort: { picked: { kind: "meta-default" }, direction: "desc" },
      });
    }
    case "entities": {
      return err({ cause: "unknown-metrics", names: [fatigueSort.data] });
    }
    default: {
      return list satisfies never;
    }
  }
}

const orderEntriesOf = (order: Query["order"]): [string, QueryOrder][] =>
  Array.isArray(order)
    ? order
    : Object.entries(order ?? {}).flatMap(
        ([member, direction]): [string, QueryOrder][] =>
          direction === undefined ? [] : [[member, direction]],
      );

export const withRank = <T>({
  cube,
  rank,
}: {
  cube: InsightsCubeQuery<T>;
  rank: EmbedRank;
}): InsightsCubeQuery<T> => {
  switch (rank.kind) {
    case "by-query-order": {
      return cube;
    }
    case "by-member": {
      return {
        ...cube,
        query: {
          ...cube.query,
          dimensions: [...(cube.query.dimensions ?? []), rank.member],
          order: [
            [rank.member, rank.direction],
            ...orderEntriesOf(cube.query.order),
          ],
        },
      };
    }
    default: {
      return rank satisfies never;
    }
  }
};

export const cardGridPlanSchema = (meta: InsightsMeta) =>
  z
    .object({
      ...SHARED_ATTRS,
      "has-multiple-ad-accounts": z
        .string()
        .optional()
        .transform((raw) => raw === "true"),
      limit: z
        .string()
        .regex(/^\d+$/)
        .transform(Number)
        .default(MAX_FILTERED_ADS),
    })
    .and(cardMetricsAttrs.transform((metrics) => ({ metrics })))
    .transform(withDates)
    .transform((attrs) =>
      toPlan({
        meta,
        list: { kind: "ads", view: meta.views.ad },
        attrs: {
          ...sharedOf({ attrs, grain: "ad" }),
          metrics: attrs.metrics,
          pageSize: attrs.limit,
          emptyMessage: noAdsMessage(attrs["has-multiple-ad-accounts"]),
        },
        placement: "card-metric",
      }),
    );

export const tablePlanSchema = (meta: InsightsMeta) =>
  z
    .object({
      ...SHARED_ATTRS,
      "entity-type": grainSchema.default("campaign"),
      metrics: tableMetricsAttr,
      limit: z
        .string()
        .regex(/^[1-9]\d*$/)
        .transform(Number)
        .default(10),
    })
    .transform(withDates)
    .transform((attrs) =>
      toPlan({
        meta,
        list: toEntityList({ grain: attrs["entity-type"], meta }),
        attrs: {
          ...sharedOf({ attrs, grain: attrs["entity-type"] }),
          metrics: attrs.metrics,
          pageSize: attrs.limit,
          emptyMessage: EMBED_EMPTY_TABLE,
        },
        placement: "default-metric",
      }),
    );

export const toPlanResult = <Plan>(
  parsed: z.ZodSafeParseResult<Result<Plan, EmbedFailure>>,
): Result<Plan, EmbedFailure> =>
  parsed.success ? parsed.data : err({ cause: "invalid-attributes" });

function toPickedMetrics({
  view,
  picked,
  placement,
}: {
  view: ViewMeta;
  picked: Picked<NonEmptyArray<string>>;
  placement: MetricColumn["place"][number];
}): Result<readonly MetricColumn[], EmbedFailure> {
  switch (picked.kind) {
    case "meta-default": {
      return ok(
        view.metrics.filter((column) => column.place.includes(placement)),
      );
    }
    case "picked": {
      const columns = picked.value.flatMap((name) =>
        view.metrics.filter(
          (column) => column.member === `${view.name}.${name}`,
        ),
      );
      const [unknown, ...others] = picked.value.filter(
        (name) =>
          !view.metrics.some(
            (column) => column.member === `${view.name}.${name}`,
          ),
      );
      return unknown === undefined
        ? ok(columns)
        : err({ cause: "unknown-metrics", names: [unknown, ...others] });
    }
    default: {
      return picked satisfies never;
    }
  }
}

function toPickedSort({
  view,
  picked,
}: {
  view: ViewMeta;
  picked: Picked<string>;
}): Result<MetricKey, EmbedFailure> {
  switch (picked.kind) {
    case "meta-default": {
      return ok(defaultSortOf(view));
    }
    case "picked": {
      const column = view.metrics.find(
        (metric) => metric.member === `${view.name}.${picked.value}`,
      );
      return column === undefined
        ? err({ cause: "unknown-metrics", names: [picked.value] })
        : ok(column.key);
    }
    default: {
      return picked satisfies never;
    }
  }
}

const equals = (member: string, values: readonly string[]): Filter => ({
  member,
  operator: "equals",
  values: [...values],
});

const GRAINS: Record<"campaigns" | "ad-sets" | "ads", Grain> = {
  campaigns: "campaign",
  "ad-sets": "ad_set",
  ads: "ad",
};

const toCubeFilter = ({
  view,
  filter,
}: {
  view: ViewMeta;
  filter: EmbedFilter;
}): Result<Filter, EmbedFailure> => {
  switch (filter.kind) {
    case "campaigns":
    case "ad-sets":
    case "ads": {
      const grain = GRAINS[filter.kind];
      const entity = entityOf({ view, grain });
      return entity.kind === "not-found"
        ? err({ cause: "filter-not-available", grain })
        : ok(equals(entity.value.id, filter.ids));
    }
    case "status": {
      return ok(equals(view.layouts.table.status, [filter.value]));
    }
    case "tag-value": {
      const [first, ...rest] = view.folders
        .flatMap((folder) => folder.members)
        .flatMap((member) =>
          member.kind === "creative-tag"
            ? [equals(member.name, [filter.value])]
            : [],
        );
      return first === undefined
        ? err({ cause: "no-creative-tags" })
        : ok({ or: [first, ...rest] });
    }
    default: {
      return filter satisfies never;
    }
  }
};

function toCubeFilters({
  view,
  filters,
}: {
  view: ViewMeta;
  filters: readonly EmbedFilter[];
}): Result<readonly Filter[], EmbedFailure> {
  const results = filters.map((filter) => toCubeFilter({ view, filter }));
  const failed = results.find(isErr);
  return failed === undefined
    ? ok(results.flatMap((result) => (isOk(result) ? [result.value] : [])))
    : failed;
}

export const toEntityList = ({
  grain,
  meta,
}: {
  grain: PerformanceRow["entityType"];
  meta: InsightsMeta;
}): EntityList => {
  switch (grain) {
    case "ad": {
      return { kind: "ads", view: meta.views.ad };
    }
    case "ad_set": {
      return { kind: "entities", grain, view: meta.views.ad_set };
    }
    case "campaign": {
      return { kind: "entities", grain, view: meta.views.campaign };
    }
    default: {
      return grain satisfies never;
    }
  }
};

type EmbedSort = { picked: Picked<string>; direction: SortDirection };

type EmbedQueryAttrs = {
  sync: SyncScope;
  dates: InsightsDateRange;
  filters: readonly EmbedFilter[];
  sort: EmbedSort;
};

export function toEmbedQuery({
  view,
  meta,
  attrs,
  segments,
}: {
  view: ViewMeta;
  meta: InsightsMeta;
  attrs: EmbedQueryAttrs;
  segments: AdsQuery["segments"];
}): Result<AdsQuery, EmbedFailure> {
  const sync = meta.syncs.find(({ id }) => id === attrs.sync.syncId);
  if (sync === undefined) {
    return err({ cause: "sync-not-connected" });
  }
  const sortBy = toPickedSort({ view, picked: attrs.sort.picked });
  const filters = toCubeFilters({ view, filters: attrs.filters });
  if (isErr(sortBy)) {
    return sortBy;
  }
  if (isErr(filters)) {
    return filters;
  }
  return ok({
    scope: [
      {
        channel: sync.channel,
        syncs: [attrs.sync],
      },
    ],
    conversionEvents: meta.defaultEvents,
    dates: attrs.dates,
    filters: filters.value,
    order: { key: sortBy.value, direction: attrs.sort.direction },
    segments,
  });
}

export const toEmbedPlanState = <Plan>({
  meta,
  toPlan,
}: {
  meta: InsightsMetaResult;
  toPlan: (meta: InsightsMeta) => Result<Plan, EmbedFailure>;
}): EmbedPlanState<Plan> => {
  switch (meta.status) {
    case "loading": {
      return { status: "loading" };
    }
    case "failed": {
      return { status: "failed", failure: meta.failure };
    }
    case "loaded": {
      if (meta.value === "no-connected-channels") {
        return { status: "no-connected-channels" };
      }
      const plan = toPlan(meta.value);
      return isErr(plan)
        ? { status: "failed", failure: plan.error }
        : { status: "planned", plan: plan.value };
    }
    default: {
      return meta satisfies never;
    }
  }
};
export type DrillEmbedAttrs = {
  grain: Grain;
  id: string;
  scope: DrillScopeArgs;
};

const DRILL_ATTRS = {
  "fivetran-sync-id": z.string().min(1),
  "date-from": z.iso.date(),
  "date-to": z.iso.date(),
  "account-id": accountAttr,
};

type DrillAttrs = z.output<z.ZodObject<typeof DRILL_ATTRS>>;

const toDrillScopeArgs = (
  attrs: DrillAttrs & { dates: InsightsDateRange },
): DrillScopeArgs => ({
  sync: toSyncScope({
    syncId: attrs["fivetran-sync-id"],
    account: attrs["account-id"],
  }),
  dates: attrs.dates,
  conversionEvents: "meta-defaults",
  list: "view-defaults",
});

const ID_ATTR = {
  ad: "ad-id",
  ad_set: "ad-set-id",
  campaign: "campaign-id",
} as const satisfies Record<Grain, string>;

export const drilldownAttrsSchema = (grain: Grain) =>
  z
    .object({ ...DRILL_ATTRS, [ID_ATTR[grain]]: z.string().min(1) })
    .transform(withDates)
    .transform(
      (attrs): DrillEmbedAttrs => ({
        grain,
        id: attrs[ID_ATTR[grain]],
        scope: toDrillScopeArgs(attrs),
      }),
    );
